import { useState, FormEvent } from 'react';
import { useElementScrollAnimation } from '../../../hooks/useScrollAnimation';

const LOGO_URL = 'https://static.readdy.ai/image/0227718025500d4b5e94dda25ab00f4a/d587ea3beae87ba159093a83d7a76a28.jpeg';
const FORM_ACTION = 'https://readdy.ai/api/form/d6ui3ue7ipaugi04l9k0';

const quickLinks = ['홈', '소개', '프로그램', '강사진', '스케줄'];
const socialLinks = [
  { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://www.instagram.com/chan_actors?igsh=MW9hYmMzN29yc2Noaw==', target: '_blank', img: null },
  { icon: 'ri-youtube-line', label: 'YouTube', href: 'https://www.youtube.com/channel/UCFNAiF7UfmFZzAmOj9ZZE4A', target: '_blank', img: null },
  { icon: 'ri-blog-line', label: 'Blog', href: 'https://blog.naver.com/chanactors', target: '_blank', img: 'naver' },
];

const contactInfo = [
  { icon: 'ri-map-pin-line', label: '경기도 양주시 옥정로 214 피오피타워 603호' },
  { icon: 'ri-phone-line', label: '031-866-9596' },
  { icon: 'ri-mail-line', label: 'chan-actors@naver.com' },
  { icon: 'ri-time-line', label: '평일 16:00 - 22:00 / 토요일 14:00 - 20:00' },
];

export default function ContactSection() {
  const [formState, setFormState] = useState({ name: '', email: '', phone: '', program: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [newsletter, setNewsletter] = useState('');
  const containerRef = useElementScrollAnimation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formState.message.length > 500) return;
    try {
      const body = new URLSearchParams();
      Object.entries(formState).forEach(([k, v]) => body.append(k, v));
      await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  const handleNavClick = (label: string) => {
    const map: Record<string, string> = {
      홈: 'home', 소개: 'about', 프로그램: 'programs', 강사진: 'instructors', 스케줄: 'schedule',
    };
    const el = document.getElementById(map[label]);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="contact" className="bg-black" ref={containerRef}>
      {/* Contact Form Area */}
      <div className="py-24 bg-black border-t border-neutral-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Info */}
            <div className="animate-on-scroll-left">
              <span className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-4 block">Contact</span>
              <h2 className="font-serif-kr font-black text-white text-4xl md:text-5xl leading-tight mb-6">
                지금 바로<br />시작하세요
              </h2>
              <p className="text-neutral-400 text-sm leading-relaxed mb-10">
                무료 체험 수업 신청부터 프로그램 문의까지, 편하게 연락 주세요.
                전문 상담원이 친절하게 안내해 드립니다.
              </p>

              {/* Contact Info */}
              <div className="space-y-4 mb-10">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-900/30 flex-shrink-0">
                      <i className={`${item.icon} text-red-500`} />
                    </div>
                    <span className="text-neutral-400 text-sm pt-1.5">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Map placeholder */}
              <div className="w-full h-48 rounded-xl overflow-hidden bg-neutral-900">
                <iframe
                  src="https://maps.google.com/maps?q=37.820594,127.092145&z=18&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="찬연기아카데미 위치 - 경기도 양주시 옥정로 214 POP TOWER"
                />
              </div>
            </div>

            {/* Right: Form */}
            <div className="animate-on-scroll-right animate-delay-200">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-900/30 mb-4">
                    <i className="ri-check-line text-red-500 text-3xl" />
                  </div>
                  <h3 className="font-serif-kr font-bold text-white text-2xl mb-2">신청이 완료되었습니다!</h3>
                  <p className="text-neutral-400 text-sm">빠른 시일 내에 연락 드리겠습니다.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 px-5 py-2.5 rounded-full bg-white text-black text-sm cursor-pointer whitespace-nowrap"
                  >
                    다시 문의하기
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-neutral-900 rounded-2xl p-8 border border-neutral-800"
                >
                  <h3 className="font-serif-kr font-bold text-white text-xl mb-6">무료 상담 &amp; 체험 신청</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs text-neutral-400 font-medium mb-1.5 block">이름 *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="홍길동"
                        value={formState.name}
                        onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-red-700 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-neutral-400 font-medium mb-1.5 block">연락처 *</label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="010-0000-0000"
                        value={formState.phone}
                        onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-red-700 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-neutral-400 font-medium mb-1.5 block">이메일</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="example@email.com"
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-red-700 transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-neutral-400 font-medium mb-1.5 block">관심 프로그램</label>
                    <select
                      name="program"
                      value={formState.program}
                      onChange={(e) => setFormState({ ...formState, program: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-white focus:outline-none focus:border-red-700 transition-colors cursor-pointer"
                    >
                      <option value="">선택해 주세요</option>
                      <option value="입시반">입시반 (예고·예대 입시)</option>
                      <option value="취미반">취미반</option>
                      <option value="전문배우반">전문배우반</option>
                      <option value="기타">기타 문의</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="text-xs text-neutral-400 font-medium mb-1.5 block">
                      문의사항 <span className="text-neutral-600">({formState.message.length}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="궁금한 점이나 요청사항을 적어주세요..."
                      value={formState.message}
                      onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-red-700 transition-colors resize-none"
                    />
                    {formState.message.length > 500 && (
                      <p className="text-red-500 text-xs mt-1">500자를 초과할 수 없습니다.</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={formState.message.length > 500}
                    className="w-full py-3 rounded-full bg-red-800 text-white font-semibold text-sm hover:bg-red-700 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    상담 신청하기 <i className="ri-arrow-right-line ml-1" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-neutral-950 py-16 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img src={LOGO_URL} alt="찬연기아카데미" className="w-full h-full object-cover" />
                </div>
                <span className="font-serif-kr font-bold text-white text-sm">찬연기아카데미</span>
              </div>
              <p className="text-neutral-500 text-xs leading-relaxed">
                연기학원의 새로운 기준. 입시부터 취미, 전문 배우 양성까지 체계적인 커리큘럼으로 여러분의 꿈을 지원합니다.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">빠른 링크</h4>
              <ul className="space-y-2">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    <button
                      onClick={() => handleNavClick(link)}
                      className="text-neutral-500 hover:text-white text-xs transition-colors cursor-pointer"
                    >
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">소셜 미디어</h4>
              <ul className="space-y-2">
                {socialLinks.map((s, i) => (
                  <li key={i}>
                    <a
                      href={s.href}
                      target={s.target}
                      rel="nofollow"
                      className="flex items-center gap-2 text-neutral-500 hover:text-white text-xs transition-colors cursor-pointer"
                    >
                      {s.img === 'naver' ? (
                        <span className="w-4 h-4 flex items-center justify-center rounded-sm bg-green-500 text-white font-black text-xs leading-none flex-shrink-0" style={{ fontSize: '10px' }}>N</span>
                      ) : (
                        <i className={`${s.icon} text-sm`} />
                      )}
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-white text-sm font-semibold mb-2">뉴스레터 구독</h4>
              <p className="text-neutral-500 text-xs mb-4">수련 팁과 학원 소식을 받아보세요</p>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  name="newsletter-email"
                  placeholder="이메일 입력"
                  value={newsletter}
                  onChange={(e) => setNewsletter(e.target.value)}
                  className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-xs placeholder-neutral-500 focus:outline-none focus:border-neutral-500 min-w-0"
                />
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-red-800 text-white hover:bg-red-900 transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-arrow-right-line text-sm" />
                </button>
              </div>
            </div>
          </div>

          {/* Brand Watermark */}
          <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-neutral-600 text-xs">
              © 찬연기아카데미학원. All rights reserved.
            </p>
            <p className="text-neutral-700 text-xs">
              사업자등록번호: 463-92-01898 | 대표: 안선애 | 학원설립 운영등록 제 1305호
            </p>
          </div>

          {/* Giant Brand Name */}
          <div className="mt-8 overflow-hidden">
            <p className="font-serif-kr font-black text-neutral-800 text-6xl md:text-8xl lg:text-9xl tracking-tight opacity-30 whitespace-nowrap">
              찬연기아카데미
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
}
